import { Component, Ref } from 'vue'

import {
  DetailType,
  IncidentAttributeId,
  IncidentId,
} from '@collector/sportsapi-client-legacy'
import { ProbableIncident } from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'

import { IncidentDetailsFillState } from '../../../enums'

export type ListOption = {
  name: string
  value: string | number
}

export type OnSelectOptions<T extends IncidentAttributeId> = {
  addAttribute: (attributeId: T) => void
  attributes: (T | AttributeConfig<T>)[]
  removeAttribute: (attributeId: T) => void
  selectedAttributes: Readonly<Ref<readonly T[]>>
  toggleAttribute: (attributeId: T) => void
  attributeId: T
}

export function isAttributeConfig<T extends IncidentAttributeId>(
  attribute: T | AttributeConfig<T>,
): attribute is AttributeConfig<T> {
  return (attribute as AttributeConfig<T>).attributeId !== undefined
}

export function getAttributeId<T extends IncidentAttributeId>(
  attributeOrAttributeConfig: T | AttributeConfig<T>,
): T {
  return isAttributeConfig(attributeOrAttributeConfig)
    ? attributeOrAttributeConfig.attributeId
    : attributeOrAttributeConfig
}

export type AttributeConfig<T extends IncidentAttributeId> = {
  attributeId: T
  isActive: (attributes: T[] | readonly T[]) => boolean
}

export type DetailBaseConfiguration<T extends IncidentAttributeId> = {
  name: string
  important: boolean
  fulfilledWhen?: (
    attributes: T[],
    probableIncident?: ProbableIncident,
  ) => boolean
  activeWhen?: (attributes: T[]) => boolean
  activeWith?: T
}

export type DetailInputConfiguration<T extends IncidentAttributeId> =
  DetailBaseConfiguration<T> & {
    type: DetailType.Input
    min?: number
    max?: number
    placeholder: string
  }

export type DetailButtonsConfiguration<T extends IncidentAttributeId> =
  DetailBaseConfiguration<T> &
    DetailBaseConfigAttributes<T> & {
      type: DetailType.Button
      cols?: number
      multiple?: boolean
      onSelect?: (options: OnSelectOptions<T>) => void
    }

export type DetailBaseConfigAttributes<T extends IncidentAttributeId> = {
  attributes: (T | AttributeConfig<T>)[]
}

export type DetailSelectConfiguration<T extends IncidentAttributeId> =
  DetailBaseConfiguration<T> &
    DetailBaseConfigAttributes<T> & {
      type: DetailType.Select
      optionsFactory: (linkedIncident: ProbableLinkedIncident) => ListOption[]
      multiple?: boolean
    }

export type DetailConfiguration<T extends IncidentAttributeId> =
  | DetailInputConfiguration<T>
  | DetailButtonsConfiguration<T>
  | DetailSelectConfiguration<T>

export type DetailsComponentConfiguration = {
  component: Component
  getIncidentDetailsFillState: (incident: ProbableIncident) => {
    state: IncidentDetailsFillState
  }
}

export type IncidentDetailsConfiguration<T extends IncidentAttributeId> =
  DetailConfiguration<T>[] | DetailsComponentConfiguration

export type SportDetailsConfiguration<
  T extends IncidentId,
  K extends IncidentAttributeId,
> = { [key in T]?: IncidentDetailsConfiguration<K> }

export type SportAttributesConfiguration<
  T extends IncidentId,
  K extends IncidentAttributeId,
> = { [key in T]?: K[] }
